import React from "react";
import * as XLSX from "xlsx";
import FileSaver from "file-saver";
import useLeadDashboard from "./hooks/useLeadDashboard";
import {
  Panel,
  HBars,
  Funnel,
  Bars,
  Sparkline,
  RateBars,
  TargetRing,
  SEGMENT_COLORS,
} from "./components/charts";
import {
  StatCard,
  Badge,
  RoleSegmentedControl,
  PeriodControl,
  PERIOD_LABEL,
} from "./components/ui";
import "./leadDashboard.css";

const AGENT_TARGET = 10; // monthly conversion goal

const ACTION_VERB = {
  created: "created a lead",
  comment: "logged a call",
  status_change: "updated a lead",
  follow_up_set: "set a follow-up",
  closed: "closed a lead",
  reopened: "reopened a lead",
  converted: "converted",
};
const FEED_TONE = {
  created: "var(--mt-theme-purple)",
  comment: "var(--mt-theme-blue)",
  follow_up_set: "var(--mt-theme-orange)",
  closed: "var(--mt-theme-red)",
  reopened: "var(--mt-muted)",
  converted: "var(--mt-theme-green)",
  status_change: "var(--mt-ink-soft)",
};

function fmtTime(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString([], { day: "2-digit", month: "short" });
  } catch {
    return "";
  }
}

function LeadDashboard({ allowedRoles }) {
  const rolesKey = allowedRoles ? allowedRoles.join(",") : "";
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableRoles = React.useMemo(() => allowedRoles, [rolesKey]);

  const {
    role,
    setRole,
    period,
    setPeriod,
    source,
    setSource,
    owner,
    setOwner,
    loading,
    error,
    refetch,
    timer,
    kpis,
    funnel,
    segments,
    sourcesData,
    states,
    actionsTrend,
    conversionsTrend,
    conversions,
    feed,
    sourceOptions,
    leaderboard,
    byOwner,
  } = useLeadDashboard(stableRoles);

  const k = kpis || {};
  const per = PERIOD_LABEL[period] || "today";
  const contextLine = {
    manager: `Team view · ${leaderboard.length || 0} field agents`,
    agent: "My leads",
    admin: "Org view · all sources & channels",
  }[role];

  const dash = (v) => (loading ? "—" : v);

  // Client-side Excel export of the leaderboard (no backend involved).
  const exportLeaderboard = () => {
    if (!leaderboard.length) return;
    const rows = leaderboard.map((a, idx) => ({
      Rank: idx + 1,
      Agent: a.name,
      Region: a.region || "",
      Calls: a.calls,
      Worked: a.worked,
      Conversions: a.conversions,
      Closed: a.closed,
      "Conversion %": a.conversion_rate,
      Overdue: a.overdue,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = { Sheets: { Leaderboard: ws }, SheetNames: ["Leaderboard"] };
    const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });
    const stamp = new Date().toISOString().slice(0, 10);
    FileSaver.saveAs(blob, `agent-leaderboard-${period}-${stamp}.xlsx`);
  };

  const emptyHint = (text) => (
    <div style={{ color: "var(--mt-muted)", fontSize: "var(--mt-text-sm)", padding: "8px 0" }}>
      {loading ? "Loading…" : text}
    </div>
  );

  // ---- header ----
  const Header = (
    <div
      style={{
        display: "flex",
        alignItems: "flex-end",
        gap: 16,
        flexWrap: "wrap",
        marginBottom: 18,
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span
            style={{
              width: 30,
              height: 30,
              borderRadius: 8,
              background: "var(--mt-theme-red-bg)",
              color: "var(--mt-theme-red)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <i className="bi bi-speedometer2" style={{ fontSize: 17 }} aria-hidden="true" />
          </span>
          <h1
            style={{
              margin: 0,
              fontSize: "var(--mt-text-xl)",
              fontWeight: "var(--mt-fw-bold)",
              color: "var(--mt-ink)",
              letterSpacing: "var(--mt-tracking-tight)",
            }}
          >
            Lead Dashboard
          </h1>
        </div>
        <span
          style={{ fontSize: "var(--mt-text-sm)", color: "var(--mt-muted)", paddingLeft: 40 }}
        >
          {contextLine}
        </span>
      </div>

      <div
        style={{
          marginLeft: "auto",
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
        }}
      >
        <PeriodControl value={period} onChange={setPeriod} />
        <RoleSegmentedControl value={role} onChange={setRole} allowedRoles={stableRoles} />
        <select
          aria-label="Filter by source"
          value={source}
          onChange={(e) => setSource(e.target.value)}
          style={{
            width: 160,
            padding: "7px 12px",
            borderRadius: "var(--mt-radius-pill)",
            border: "1px solid var(--mt-border)",
            background: "var(--mt-surface)",
            color: "var(--mt-ink)",
            fontFamily: "inherit",
            fontSize: "var(--mt-text-sm)",
            cursor: "pointer",
          }}
        >
          <option value="All">All sources</option>
          {sourceOptions.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          aria-label="Filter by owner"
          value={owner}
          onChange={(e) => setOwner(e.target.value)}
          style={{
            width: 180,
            padding: "7px 12px",
            borderRadius: "var(--mt-radius-pill)",
            border: "1px solid var(--mt-border)",
            background: "var(--mt-surface)",
            color: "var(--mt-ink)",
            fontFamily: "inherit",
            fontSize: "var(--mt-text-sm)",
            cursor: "pointer",
          }}
        >
          <option value="All">All owners</option>
          {(byOwner || []).map((o) => (
            <option key={o.id ?? "unassigned"} value={o.id ?? "unassigned"}>
              {o.label} ({o.value})
            </option>
          ))}
        </select>
        <button
          type="button"
          className="mt-click iconbtn"
          onClick={refetch}
          aria-label="Refresh"
          title="Refresh"
          style={{
            width: 36,
            height: 34,
            border: "1.5px solid var(--mt-action-fg)",
            borderRadius: "var(--mt-radius-pill)",
            background: "var(--mt-surface)",
            color: "var(--mt-action-fg)",
            cursor: "pointer",
          }}
        >
          <i className={`bi bi-arrow-clockwise${loading ? " spin" : ""}`} aria-hidden="true" />
        </button>
        <span
          style={{
            fontSize: "var(--mt-text-sm)",
            color: "var(--mt-muted)",
            fontVariantNumeric: "tabular-nums",
            minWidth: 22,
            textAlign: "center",
          }}
        >
          {timer}
        </span>
      </div>
    </div>
  );

  if (error && !loading) {
    return (
      <div className="lead-dash">
        <div className="lead-dash__panel">
          {Header}
          <Panel title="Couldn't load dashboard">
            <div style={{ color: "var(--mt-muted)", fontSize: "var(--mt-text-sm)" }}>{error}</div>
            <button
              type="button"
              className="mt-click"
              onClick={refetch}
              style={{
                alignSelf: "flex-start",
                padding: "6px 14px",
                borderRadius: "var(--mt-radius-pill)",
                border: "none",
                background: "var(--mt-theme-red)",
                color: "#fff",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Retry
            </button>
          </Panel>
        </div>
      </div>
    );
  }

  // ---- KPI row ----
  const actUp = (k.actions_delta ?? 0) >= 0;
  const KPIs = (
    <div
      className="lead-dash__kpis"
      style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 14, marginBottom: 16 }}
    >
      <StatCard
        label={`Actions · ${per}`}
        value={dash((k.actions ?? 0).toLocaleString("en-IN"))}
        stripe="green"
        delta={`${actUp ? "+" : ""}${k.actions_delta ?? 0}% vs prev`}
        deltaDirection={actUp ? "up" : "down"}
      />
      <StatCard
        label={`Conversions · ${per}`}
        value={dash(k.conversions ?? 0)}
        stripe="green"
        delta={`${k.conversion_rate ?? 0}% of worked · paid subs`}
      />
      <StatCard
        label={`Leads Closed · ${per}`}
        value={dash(k.closed ?? 0)}
        stripe="blue"
        delta={`${k.won ?? 0} won · ${k.lost ?? 0} lost`}
      />
      <StatCard
        label={`New Leads · ${per}`}
        value={dash(k.new_leads ?? 0)}
        stripe="blue"
        delta="first-contact backlog"
      />
      <StatCard
        label="Follow-ups Due"
        value={dash(k.due ?? 0)}
        stripe="green"
        delta={`${k.overdue ?? 0} overdue`}
        deltaDirection={(k.overdue ?? 0) > 0 ? "down" : undefined}
      />
    </div>
  );

  // ---- Row 1: trends + funnel ----
  const Row1 = (
    <div
      className="lead-dash__row1"
      style={{ display: "grid", gridTemplateColumns: "1.7fr 1fr", gap: 14, marginBottom: 14 }}
    >
      <Panel title="Activity & Conversions" hint="last 14 days">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
          <div>
            <div
              style={{
                fontSize: "var(--mt-text-xs)",
                color: "var(--mt-muted)",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                marginBottom: 8,
              }}
            >
              Actions / day
            </div>
            {actionsTrend.length ? <Bars data={actionsTrend} /> : emptyHint("No activity")}
          </div>
          <div>
            <div
              style={{
                fontSize: "var(--mt-text-xs)",
                color: "var(--mt-muted)",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                marginBottom: 8,
              }}
            >
              Paid conversions / day
            </div>
            {conversionsTrend.length ? (
              <Sparkline data={conversionsTrend} />
            ) : (
              emptyHint("No conversions")
            )}
          </div>
        </div>
      </Panel>
      <Panel title="Funnel" hint={PERIOD_LABEL[period]}>
        {funnel.length ? <Funnel stages={funnel} /> : emptyHint("No data")}
      </Panel>
    </div>
  );

  // ---- Row 2: distributions ----
  const Row2 = (
    <div
      className="lead-dash__row2"
      style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 1fr", gap: 14, marginBottom: 14 }}
    >
      <Panel title="By Source" hint="volume · conversion rate">
        {sourcesData.length ? <RateBars items={sourcesData} /> : emptyHint("No data")}
      </Panel>
      <Panel title="By Segment" hint="buyer interest">
        {segments.some((s) => s.value) ? (
          <HBars items={segments} colorMap={SEGMENT_COLORS} />
        ) : (
          emptyHint("No data")
        )}
      </Panel>
      <Panel title="By State" hint="top 6">
        {states.length ? <HBars items={states} color="var(--mt-theme-red)" /> : emptyHint("No data")}
      </Panel>
    </div>
  );

  // ---- Ownership: who is actively handling how many leads ----
  const Ownership = (
    <div style={{ marginBottom: 14 }}>
      <Panel title="Ownership" hint="leads being handled · top 8 · latest actioner">
        {byOwner && byOwner.length ? (
          <HBars items={byOwner.slice(0, 8)} color="var(--mt-theme-red)" />
        ) : (
          emptyHint("No owned leads")
        )}
      </Panel>
    </div>
  );

  // ---- Live activity feed ----
  const Feed = (
    <Panel title="Live Activity" hint={`${PERIOD_LABEL[period]} · from LeadActivity`} style={{ maxHeight: 380 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 2, overflowY: "auto", margin: "-2px -4px", padding: "2px 4px" }}>
        {feed.length === 0
          ? emptyHint("No activity logged")
          : feed.map((f) => (
              <div
                key={f.id}
                style={{ display: "flex", gap: 10, alignItems: "center", padding: "8px 6px", borderRadius: "var(--mt-radius-row)" }}
              >
                <span
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: 7,
                    flex: "none",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12,
                    color: "#fff",
                    background: FEED_TONE[f.action] || "var(--mt-muted)",
                  }}
                  aria-hidden="true"
                >
                  {f.icon}
                </span>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: "var(--mt-text-sm)", lineHeight: 1.35 }}>
                    <b>{f.actor}</b> {ACTION_VERB[f.action] || f.action}
                    {f.name ? (
                      <>
                        {" · "}
                        <span style={{ color: "var(--mt-ink-soft)" }}>{f.name}</span>
                      </>
                    ) : null}
                  </div>
                  {f.detail && (
                    <div
                      style={{
                        fontSize: "var(--mt-text-xs)",
                        color: "var(--mt-muted)",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {f.detail}
                    </div>
                  )}
                </div>
                <span
                  style={{
                    marginLeft: "auto",
                    fontSize: "var(--mt-text-xs)",
                    color: "var(--mt-muted)",
                    whiteSpace: "nowrap",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {fmtTime(f.created_at)}
                </span>
              </div>
            ))}
      </div>
    </Panel>
  );

  // ---- Bottom: leaderboard (team/admin) or snapshot (agent) ----
  const th = (label, align) => (
    <th
      key={label}
      style={{
        padding: "8px 10px",
        fontSize: "var(--mt-text-xs)",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.03em",
        color: "var(--mt-muted)",
        textAlign: align || "right",
        borderBottom: "1px solid var(--mt-border)",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </th>
  );

  let primary;
  if (role === "agent") {
    primary = (
      <Panel title="My Snapshot" hint={PERIOD_LABEL[period]}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 18, alignItems: "center" }}>
            <TargetRing value={k.conversions ?? 0} target={AGENT_TARGET} label="conversions" />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, flex: 1, minWidth: 220 }}>
              {[
                ["Actions", k.actions ?? 0],
                ["Leads worked", k.worked ?? 0],
                ["Leads closed", k.closed ?? 0],
                ["Overdue", k.overdue ?? 0],
              ].map(([l, v]) => (
                <div
                  key={l}
                  style={{
                    background: "var(--mt-surface-alt)",
                    border: "1px solid var(--mt-border)",
                    borderRadius: "var(--mt-radius-row)",
                    padding: "12px 14px",
                  }}
                >
                  <div style={{ fontSize: "var(--mt-text-2xl)", fontWeight: 800, lineHeight: 1.1 }}>
                    {dash(v)}
                  </div>
                  <div style={{ fontSize: "var(--mt-text-xs)", color: "var(--mt-muted)", marginTop: 4 }}>
                    {l}
                  </div>
                </div>
              ))}
            </div>
          </div>
      </Panel>
    );
  } else {
    primary = (
      <Panel
        title="Agent Leaderboard"
        hint={`by conversions · ${PERIOD_LABEL[period]}`}
        action={
          leaderboard.length > 0 ? (
            <button
              type="button"
              onClick={exportLeaderboard}
              title="Download leaderboard as Excel"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 12px",
                fontSize: "var(--mt-text-xs)",
                fontWeight: 700,
                color: "var(--mt-ink)",
                background: "var(--mt-surface-alt)",
                border: "1px solid var(--mt-border)",
                borderRadius: "var(--mt-radius-row)",
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              ↓ Excel
            </button>
          ) : null
        }
      >
          {leaderboard.length === 0 ? (
            emptyHint("No agents to rank yet")
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
                <thead>
                  <tr>
                    {th("#", "center")}
                    {th("Agent", "left")}
                    {th("Calls")}
                    {th("Worked")}
                    {th("Conversions")}
                    {th("Closed")}
                    {th("Conv %", "left")}
                    {th("Overdue")}
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.map((a, idx) => (
                    <tr key={a.agent_id} style={{ borderBottom: "1px solid var(--mt-border)" }}>
                      <td style={{ padding: 10, textAlign: "center", fontWeight: 800, color: "var(--mt-muted)" }}>
                        {idx + 1}
                      </td>
                      <td style={{ padding: 10 }}>
                        <div style={{ display: "flex", flexDirection: "column" }}>
                          <span style={{ fontWeight: 700, color: "var(--mt-ink)" }}>{a.name}</span>
                          {a.region && (
                            <span style={{ fontSize: "var(--mt-text-xs)", color: "var(--mt-muted)" }}>
                              {a.region}
                            </span>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: 10, textAlign: "right", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                        {a.calls}
                      </td>
                      <td style={{ padding: 10, textAlign: "right", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                        {a.worked}
                      </td>
                      <td style={{ padding: 10, textAlign: "right", fontWeight: 800, color: "var(--mt-theme-green)", fontVariantNumeric: "tabular-nums" }}>
                        {a.conversions}
                      </td>
                      <td style={{ padding: 10, textAlign: "right", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                        {a.closed}
                      </td>
                      <td style={{ padding: 10 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 110 }}>
                          <div style={{ flex: 1, height: 6, background: "var(--mt-gray-100)", borderRadius: 4, overflow: "hidden" }}>
                            <div style={{ width: `${Math.min(a.conversion_rate, 100)}%`, height: "100%", background: "var(--mt-theme-red)", borderRadius: 4 }} />
                          </div>
                          <span style={{ fontWeight: 700, width: 40, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                            {a.conversion_rate}%
                          </span>
                        </div>
                      </td>
                      <td style={{ padding: 10, textAlign: "right" }}>
                        {a.overdue ? <Badge tone="red">{a.overdue}</Badge> : <span style={{ color: "var(--mt-muted)" }}>—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </Panel>
    );
  }

  const Conversions = (
    <Panel
      title="Recent Conversions"
      hint={`${conversions.length} · ${PERIOD_LABEL[period]}`}
      style={{ maxHeight: 380 }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          overflowY: "auto",
          margin: "-2px -4px",
          padding: "2px 4px",
        }}
      >
        {conversions.length === 0
          ? emptyHint("No conversions in this period")
          : conversions.map((c, i) => (
              <div
                key={`${c.phone}_${i}`}
                style={{
                  display: "flex",
                  gap: 10,
                  alignItems: "center",
                  padding: "9px 6px",
                  borderRadius: "var(--mt-radius-row)",
                }}
              >
                <span
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: 7,
                    flex: "none",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    background: "var(--mt-theme-green)",
                    fontSize: 13,
                  }}
                  aria-hidden="true"
                >
                  <i className="bi bi-check-lg" />
                </span>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div
                    style={{
                      fontSize: "var(--mt-text-sm)",
                      fontWeight: 700,
                      color: "var(--mt-ink)",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {c.name || c.phone}
                  </div>
                  <div
                    style={{
                      fontSize: "var(--mt-text-xs)",
                      color: "var(--mt-muted)",
                      display: "flex",
                      gap: 8,
                      flexWrap: "wrap",
                    }}
                  >
                    <span>
                      <i className="bi bi-telephone-fill" aria-hidden="true" /> {c.phone}
                    </span>
                    {c.plan && (
                      <span>
                        <i className="bi bi-tag-fill" aria-hidden="true" /> {c.plan}
                      </span>
                    )}
                    {c.agent && <span>via {c.agent}</span>}
                  </div>
                </div>
                <div style={{ marginLeft: "auto", textAlign: "right", flex: "none" }}>
                  {c.amount ? (
                    <div
                      style={{
                        fontSize: "var(--mt-text-sm)",
                        fontWeight: 800,
                        color: "var(--mt-theme-green)",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      ₹{Number(c.amount).toLocaleString("en-IN")}
                    </div>
                  ) : null}
                  <div style={{ fontSize: "var(--mt-text-xs)", color: "var(--mt-muted)" }}>
                    {fmtDate(c.converted_at)}
                  </div>
                </div>
              </div>
            ))}
      </div>
    </Panel>
  );

  const Bottom = (
    <>
      <div style={{ marginBottom: 14 }}>{primary}</div>
      <div
        className="lead-dash__row2"
        style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}
      >
        {Conversions}
        {Feed}
      </div>
    </>
  );

  return (
    <div className="lead-dash">
      <div className="lead-dash__panel">
        {Header}
        {KPIs}
        {Row1}
        {Row2}
        {Ownership}
        {Bottom}
      </div>
    </div>
  );
}

export default LeadDashboard;
